import React, { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useApiFormAuthEmail, useApiFormRegisterInvite } from '../../api-new/state/auth'
import { useAuthContext, useLogout } from '../../context/auth.context'
import { useNotificationContext } from '../../context/notification/context'
import { useLoad } from '../../shared/hooks/load'
import { Terms } from '../../shared/legacy/legal/terms'
import { AuthHead, AuthLoading, AuthNotice, AuthPage } from '../../shared/sso-workspace/auth-card'
import { authPath } from '../../layout/unauthorized/auth/hooks/auth-path'
import { useLoginRedirect } from '../../layout/unauthorized/auth/hooks/redirect'
import { passwordProblem } from '../../layout/unauthorized/auth/page/register'
import { ErrorReference } from '../../shared/error/error-reference'
import { useErrorLine } from '../../shared/error/use-error-line'
import { signInAndRemember } from '../../shared/recent-accounts/sign-in-memory'

// /public/invite/register/:key: accept an invitation to an organization.
//
// The invite already carries the roles, courses and groups the admin chose.
// How it is accepted depends on who was invited (owner direction 2026-09-27;
// spec 2026-09-27-sso-domain-registration-and-invites-design):
//   - an existing account signs in AS ITSELF and accepts -- no password is
//     ever set or changed here;
//   - a new person whose organization signs in with SSO signs in with it;
//     the key rides the sign-in (router state, then the auth service's
//     passive login as a hash) and that sign-in accepts the invite;
//   - any other new person chooses a password here (the emailed link proves
//     the mailbox).
// Keys are single use and expire: a dead one says the invitation is no
// longer valid.
//
// Accepting signs the person in and lands them like any sign-in (owner
// retest 2026-09-29: a new account ended on a bare "Register Success" page,
// signed out). A new password account is signed in through the normal
// email + password sign-in with the password just chosen -- the emailed link
// already proved the mailbox -- so its rate limits, SSO-required checks and
// sign-in record all apply and the server has no second way to mint a
// session. If that sign-in fails, the sign-in page opens with the email
// filled in.

const normalized = email => (email || '').trim().toLowerCase()

// The invite key lives in this tab only (re-review RM-1): the page takes it
// out of the address bar on arrival and finds it here again when it comes
// back after a sign-in, so no URL -- redirect parameters, sign-in URLs,
// history -- ever carries it after the emailed link itself.
const KEY_STORE = 'edxr-invite-key'

const storedKey = () => {
  try {
    return window.sessionStorage.getItem(KEY_STORE) || ''
  } catch {
    return ''
  }
}

const storeKey = key => {
  try {
    if (key) {
      window.sessionStorage.setItem(KEY_STORE, key)
    } else {
      window.sessionStorage.removeItem(KEY_STORE)
    }
  } catch {}
}

export const INVITE_PAGE = '/public/invite/register'

const messageOf = err => (err && err.data && typeof err.data.message === 'string' && err.data.message) || ''

export const InviteRegister = () => {
  const history = useHistory()
  const { key: keyInURL } = useParams()

  // The key from the link, or (back after signing in) from this tab.
  const [key] = useState(() => keyInURL || storedKey())

  const apiFormRegisterInvite = useApiFormRegisterInvite()
  const apiFormAuthEmail = useApiFormAuthEmail()
  const authContext = useAuthContext()
  const logout = useLogout()
  const loginRedirect = useLoginRedirect()
  const notificationContext = useNotificationContext()

  // loading -> invalid | sso | signin | password
  const [state, updateState] = useState({ step: 'loading', invite: null, message: '' })
  const [password, updatePassword] = useState('')
  const [revealPassword, updateRevealPassword] = useState(false)
  const [agreed, updateAgreed] = useState(false)
  const [showTerms, updateShowTerms] = useState(false)
  const [submitting, updateSubmitting] = useState(false)
  const [failure, failureError, updateFailure] = useErrorLine()

  useLoad(
    async () => {
      if (!key) {
        updateState({ step: 'invalid', invite: null, message: '' })
        return
      }

      if (keyInURL) {
        storeKey(keyInURL)
        history.replace(INVITE_PAGE)
      }

      try {
        const invite = await apiFormRegisterInvite.get(key)

        const step = invite.accountExists ? 'signin' : invite.signInWithOrganization ? 'sso' : 'password'

        updateState({ step, invite, message: '' })
      } catch (err) {
        updateState({ step: 'invalid', invite: null, message: messageOf(err) })
      }
    },
    []
  )

  const invite = state.invite
  const signedInEmail = authContext.state.isAuth ? (authContext.state.jwtClaims || {}).email : ''
  const signedInAsInvitee = !!signedInEmail && !!invite && normalized(signedInEmail) === normalized(invite.email)
  const signedInAsSomeoneElse = !!signedInEmail && !!invite && !signedInAsInvitee

  const needsPassword = state.step === 'password'
  const problem = password ? passwordProblem(password) : ''
  const ready = agreed && (!needsPassword || !passwordProblem(password))

  const signInWithOrganization = () =>
    history.push({ pathname: '/auth/providers', search: `?${new URLSearchParams({ email: invite.email })}`, state: { inviteKey: key } })

  const signInToAccept = () =>
    history.push(authPath('/auth/email', { email: invite.email, redirect: btoa(INVITE_PAGE) }))

  const handleSubmit = async e => {
    e.preventDefault()

    if (!ready || submitting) {
      return
    }

    updateSubmitting(true)
    updateFailure('')

    try {
      await apiFormRegisterInvite.put(key, needsPassword ? { password } : {})
    } catch (err) {
      const status = err && err.status

      if (status === 404) {
        storeKey('')
        updateState({ step: 'invalid', invite: null, message: messageOf(err) })
      } else if (status === 409) {
        updateState(prev => ({ ...prev, step: 'sso', message: messageOf(err) }))
      } else if (status === 403 && !signedInEmail) {
        updateState(prev => ({ ...prev, step: 'signin', message: '' }))
      } else {
        updateFailure(messageOf(err) || "We couldn't accept the invitation. Try again.", err)
      }
      updateSubmitting(false)
      return
    }

    storeKey('')

    if (needsPassword) {
      try {
        const authToken = await apiFormAuthEmail.post({ email: invite.email, password })
        signInAndRemember(authContext.setAuthToken, authToken, { method: { kind: 'password' }, typed: invite.email })
      } catch {
        history.push(authPath('/auth/email', { email: invite.email, use: 'password', reason: 'account-ready' }))
        return
      }
    }

    notificationContext.toast.notify(`You joined ${invite.organizationName || 'the organization'}.`, 5000)
    loginRedirect()
  }

  if (state.step === 'loading') {
    return (
      <AuthPage labelledBy="invite-title">
        <AuthHead id="invite-title" title="Your invitation" />
        <AuthLoading>Opening your invitation…</AuthLoading>
      </AuthPage>
    )
  }

  if (state.step === 'invalid') {
    return (
      <AuthPage labelledBy="invite-title">
        <AuthHead id="invite-title" title="Your invitation" />
        <AuthNotice tone="bad" role="alert">
          <p>This invitation is no longer valid. Invitations work once and expire after 7 days. Ask the person who invited you to send a new one, or sign in if you already have an account.</p>
        </AuthNotice>
        <button type="button" className="btn sw-btn sa-block" onClick={() => history.push('/auth/email')}>
          Go to sign in
        </button>
      </AuthPage>
    )
  }

  const organizationName = invite.organizationName || 'an organization'

  const head = <AuthHead id="invite-title" title={`Join ${organizationName}`} lede={`${organizationName} invited you to EducationXR.`} />

  const invitedAs =
    <p className="m-0">
      Invitation for <span className="sa-email">{invite.email}</span>
    </p>

  const terms =
    <div className="sa-terms">
      <div className="form-check sa-check">
        <input className="form-check-input" id="invite-terms" type="checkbox" checked={agreed} onChange={e => updateAgreed(e.target.checked)} />
        <label className="form-check-label" htmlFor="invite-terms">I agree to the terms and conditions</label>
      </div>
      <button
        type="button"
        className="btn btn-link sa-terms-toggle"
        aria-expanded={showTerms}
        aria-controls="invite-terms-body"
        onClick={() => updateShowTerms(!showTerms)}>
        {showTerms ? 'Hide the terms and conditions' : 'Read the terms and conditions'}
      </button>
      {showTerms &&
        <div className="sa-terms-body" id="invite-terms-body" tabIndex={0} role="region" aria-label="Terms and conditions">
          <Terms />
        </div>}
    </div>

  const failed = failure &&
    <AuthNotice tone="bad" role="alert">
      <p>{failure}</p>
      <ErrorReference error={failureError} />
    </AuthNotice>

  // Someone else is signed in: never accept for them.
  if (signedInAsSomeoneElse) {
    return (
      <AuthPage labelledBy="invite-title">
        {head}
        <AuthNotice tone="warn">
          <p>
            You're signed in as <strong>{signedInEmail}</strong>. This invitation is for <strong>{invite.email}</strong>: sign out, then open the invitation again and sign in as that account.
          </p>
        </AuthNotice>
        <button type="button" className="btn sw-btn sa-block" onClick={() => logout()}>
          Sign out
        </button>
      </AuthPage>
    )
  }

  if (state.step === 'sso') {
    return (
      <AuthPage labelledBy="invite-title">
        {head}

        {state.message
          ? <AuthNotice tone="warn" role="alert">
              <p>{state.message}</p>
            </AuthNotice>
          : <AuthNotice>
              <p>
                Your organization signs in with{' '}
                {invite.connectionName ? <strong>{invite.connectionName}</strong> : 'its own single sign-on'}, so there's no password to set. Sign in with your organization to accept. Your access is ready as soon as you do.
              </p>
            </AuthNotice>}

        {invitedAs}

        <button type="button" className="btn sw-btn-primary sa-block" onClick={signInWithOrganization}>
          Sign in with your organization
        </button>
      </AuthPage>
    )
  }

  if (state.step === 'signin' && !signedInAsInvitee) {
    return (
      <AuthPage labelledBy="invite-title">
        {head}
        <AuthNotice>
          <p>You already have an EducationXR account. Sign in as <strong>{invite.email}</strong> to accept, and you'll come straight back here.</p>
        </AuthNotice>
        <button type="button" className="btn sw-btn-primary sa-block" onClick={signInToAccept}>
          Sign in to accept
        </button>
      </AuthPage>
    )
  }

  return (
    <AuthPage labelledBy="invite-title">
      {head}
      {failed}
      {invitedAs}

      <form className="sa-form" noValidate onSubmit={handleSubmit}>
        {needsPassword &&
          <div className="sw-field">
            <label className="sw-label" htmlFor="invite-password">Password</label>
            <div className="input-group">
              <input
                id="invite-password"
                className="form-control"
                type={revealPassword ? 'text' : 'password'}
                autoComplete="new-password"
                aria-describedby="invite-password-hint"
                aria-invalid={!!problem}
                value={password}
                onChange={e => updatePassword(e.target.value)}
              />
              <button
                type="button"
                className="btn sw-btn"
                aria-controls="invite-password"
                aria-label={revealPassword ? 'Hide password' : 'Show password'}
                onClick={() => updateRevealPassword(!revealPassword)}>
                {revealPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <p id="invite-password-hint" className="m-0 sw-hint">
              {problem || 'Use at least 8 characters, with no spaces.'}
            </p>
          </div>}

        {terms}

        <button type="submit" className="btn sw-btn-primary sa-block" disabled={!ready || submitting}>
          {submitting ? 'Accepting…' : 'Accept invitation'}
        </button>
      </form>
    </AuthPage>
  )
}
